import { CascadesApi, Configuration } from '@sr-sdks/api-sdk-axios';
import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useQuery,
} from '@tanstack/react-query';
import { configuration } from '../../configuration';
import axiosInstance, { bypassCacheConfig } from '../../utils/axiosInstance';
import { toApiError } from '../../utils/httpError';

// ===== API CLIENT =====

/**
 * Create a cascades API instance
 */
const getCascadesApi = () => {
  return new CascadesApi(
    new Configuration(),
    configuration.PAYMENTS_API_BASE,
    axiosInstance,
  );
};

// ===== QUERY KEYS =====

/**
 * @internal **IMPORTANT**: Avoid exporting and prefer to use
 * a Query Options factory instead.
 */
const cascadesKeys = {
  all: ['cascades'] as const,
  paginated: (params: CascadeQueryParams) =>
    [...cascadesKeys.all, 'paginated', { ...params }] as const,
};

// ===== API FUNCTIONS =====

export interface CascadeQueryParams {
  beneficiaryTitleId?: string;
  page?: number;
  pageSize?: number;
  serviceId?: string;
  sortBy?: string;
}

/**
 * Default parameters for Cascades queries. Merged into
 * {@link queryOptionsGetCascades} before the query key is built, so an unset
 * page/pageSize/sortBy never creates a distinct cache entry.
 */
export const CASCADES_DEFAULT_PARAMS = {
  page: 1,
  pageSize: 50,
  sortBy: 'cascadeDate:desc',
} as const satisfies CascadeQueryParams;

/**
 * {@link fetchCascades} always has a real `serviceId` by the time it runs —
 * {@link queryOptionsGetCascades} only enables the query once one is
 * present — so it's typed as required here rather than falling back to an
 * empty string, which the backend's `@IsUUID()` validation rejects anyway.
 */
type FetchCascadesParams = Omit<CascadeQueryParams, 'serviceId'> & {
  serviceId: string;
};

/**
 * Fetch cascades with optional filters and pagination
 */
export const fetchCascades = async (params: FetchCascadesParams) => {
  try {
    const { beneficiaryTitleId, page, pageSize, serviceId, sortBy } = params;

    const response = await getCascadesApi().cascadeControllerFindAll(
      serviceId,
      beneficiaryTitleId,
      page,
      pageSize,
      sortBy,
      bypassCacheConfig(),
    );

    return response.data;
  } catch (error) {
    // Do NOT capture Sentry exceptions here; QueryCache.onError already captures.
    throw toApiError(error, {
      endpoint: 'cascadeControllerFindAll',
      params: params,
    });
  }
};

// ===== QUERY OPTIONS =====

/**
 * React Query options for a paginated cascades list.
 * Skips the fetch when `serviceId` is missing (caller opt-out).
 */
export const queryOptionsGetCascades = (params?: CascadeQueryParams) => {
  const merged = { ...CASCADES_DEFAULT_PARAMS, ...params };
  const { serviceId } = merged;

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- lint-backlog: queryKey omits a value the queryFn reads; adding it changes caching
  return queryOptions({
    queryFn: serviceId
      ? () => fetchCascades({ ...merged, serviceId })
      : skipToken,
    queryKey: cascadesKeys.paginated(merged),
  });
};

// ===== QUERY HOOKS =====

/** Narrow opt-in flags for {@link useCascades} call sites. */
export type UseCascadesOptions = {
  enabled?: boolean;
  /** Opt in for pagination UX; not set on shared query options. */
  placeholderData?: typeof keepPreviousData;
};

/**
 * Hook for fetching cascades with optional filters.
 * `options` is intentionally narrow so call sites opt into behaviour like
 * `placeholderData: keepPreviousData` without affecting every consumer.
 */
export const useCascades = (
  params?: CascadeQueryParams,
  options?: UseCascadesOptions,
) => {
  return useQuery({
    ...queryOptionsGetCascades(params),
    ...options,
  });
};
